<!-- Copyright (c) 2022 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div @click="mainContainerClicked" class="email-input-main">
    <div class="email-list" ref="emailListDiv">
      <div>{{ title }} ({{ emailList.length }})</div>
      <div class="email-container" v-for="(e, i) in emailList" :key="i" :style="emailContainerStyling(e, i)">
        <div ref="emailText" class="email-text" @click="emailClicked($event, i)" @keydown="emailKeyDown($event, i)" @blur="emailBlurred($event, i)">
          {{ e['email'] }}
        </div>
        <span v-if="!isDisabled" :style="deleteEmailSpanStyling(e, i)" @click="deleteEmailClicked(i)">
          x
        </span>
      </div>
      <div class="new-email" :contenteditable="!isDisabled" ref="newEmail" @blur="newEmailBlurred" @keydown="newEmailKeyDown"></div>
    </div>
    <div class="slot-container">
      <slot></slot>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
  },
  data: function() {
    return {
      emailList: [],

      focusedEmailIndex: -1,
      // 0 to positive infinity = focused on one of the added emails
      // -1 = nothing focused
    };
  },
  props: {
    mails: { type: Array, default: function() { return [] } }, // list of emails inputted, use .sync
    // format: [
    //   {
    //     email: "email@example.com", // string of the email input
    //     valid: true, // (OPTIONAL) indicates whether the email is valid or not, the component will validate the email and update the prop if not passed
    //   },
    //   {
    //     email: "email.com",
    //     valid: false,
    //   },
    // ]
    title: { type: String, default: 'Title' },
    isDisabled: { type: Boolean, default: false }, // controls whether the user can change the input or not
  },
  methods: {
    emailContainerStyling: function(email, index) {
      let color;
      let backgroundColor;
      if (this.focusedEmailIndex === index) {
        color = "black";
        backgroundColor = "lightgray";
      } else if (email.valid) {
        color = "black";
        backgroundColor = "limegreen";
      } else {
        color = "white";
        backgroundColor = "indianred";
      }

      return {
        color,
        backgroundColor,
      };
    },
    deleteEmailSpanStyling: function(email, index) {
      return {
        color: this.focusedEmailIndex === index || email.valid ? "red" : "lightgray",
        cursor: "pointer",
      };
    },

    mainContainerClicked: function() {
      this.focusOnNewEmailInput();
    },
    focusOnEmailText: function(index) {
      this.focusedEmailIndex = index;

      this.$refs.emailText[index].contentEditable = true; // contentEditable needs to be toggled via JS
      // otherwise there'll be a bug where clicking outside the component will focus on the emails
      // https://stackoverflow.com/a/34445203

      this.$nextTick(() => { // wait for contentEditable to take effect
        this.$refs.emailText[index].focus();
      });
    },
    blurOffEmailText: function(index) {
      this.$refs.emailText[index].blur();
    },
    focusOnNewEmailInput: function() {
      this.$refs.newEmail.focus();
    },
    shiftEmailTextFocusLeft: async function(currentIndex) {
      if (currentIndex === -1) { // if currently focused on new email input
        if (this.emailList.length !== 0) {
          this.focusOnEmailText(this.emailList.length - 1); // focus onto the last email
        }

        return;
      }

      this.blurOffEmailText(currentIndex); // emailBlurred will check whether the div should be removed or not, and will update email validity if necessary
      await this.$nextTick(); // wait for emailBlurred to finish removing the emails and updating the UI

      if (this.emailList.length === 0) { // if there are no existing emails to focus on
        this.focusOnNewEmailInput();
      } else if (currentIndex === 0) { // focus was already on the leftmost email
        this.focusOnEmailText(0); // refocus onto the first email
      } else {
        this.focusOnEmailText(currentIndex - 1);
      }

      // ============= caret position?
    },
    shiftEmailTextFocusRight: async function(currentIndex) {
      const originalNumberOfEmailTexts = this.emailList.length;

      this.blurOffEmailText(currentIndex); // emailBlurred will check whether the div should be removed or not, and will update email validity if necessary
      // await this.$nextTick(); // wait for emailBlurred to finish removing the emails and updating the UI

      if (this.emailList.length === originalNumberOfEmailTexts) { // no emails were removed
        if (currentIndex === this.emailList.length - 1) { // focus was already on the rightmost email
          this.focusOnNewEmailInput();
        } else {
          this.focusOnEmailText(currentIndex + 1);
        }
      } else if (this.emailList.length > 0) { // an email was removed, but there are still existing email texts to focus on
        if (currentIndex <= this.emailList.length - 1) { // focus wasn't on the rightmost email
          this.focusOnEmailText(currentIndex);
        } else {
          this.focusOnNewEmailInput();
        }
      } else { // an email was removed, and there are no more email texts to focus on
        this.focusOnNewEmailInput();
      }
    },
    emailClicked: function(pointerEvent, index) {
      pointerEvent.stopPropagation(); // to prevent the click handler on email input main from being triggered
      if (!this.isDisabled) {
        this.focusOnEmailText(index);
      }
    },
    emailKeyDown: async function(keyboardEvent, index) {
      const keyPressed = keyboardEvent.key;
      const shiftHeldDown = keyboardEvent.shiftKey;
      const inputtedEmail = keyboardEvent.target.innerText;
      
      switch (keyPressed) {
        case 'Enter':
        case ';':
          keyboardEvent.preventDefault(); // prevent the keystroke from inputting a character into the div
          this.shiftEmailTextFocusRight(index);
          break;

        case 'Backspace':
          if (!inputtedEmail) { // if the div is blank when backspace is pressed
            this.shiftEmailTextFocusLeft(index);
          }
          break;

        case 'Delete':
          if (!inputtedEmail) { // if the div is blank when delete is pressed
            this.shiftEmailTextFocusRight(index);
          }
          break;

        case 'Tab':
          if (index === 0 && shiftHeldDown) { // focus is on the leftmost email, and shift tab is pressed
            break; // allow keystroke to move focus out of the component
          }
          // no need to check for rightmost email and tab presses, new email will be there

          keyboardEvent.preventDefault(); // prevent tab from moving focus out of the component
          // and instead shift focus to another field within the component
          if (shiftHeldDown) {
            this.shiftEmailTextFocusLeft(index);
          } else {
            this.shiftEmailTextFocusRight(index);
          }
          break;
        
        case ' ':
          keyboardEvent.preventDefault(); 
          break;
      }
    },
    emailBlurred: function(focusEvent, index) {
      this.focusedEmailIndex = -1;
      this.$refs.emailText[index].contentEditable = false; // contentEditable needs to be toggled via JS
      // otherwise there'll be a bug where clicking outside the component will focus on the emails
      // https://stackoverflow.com/a/34445203

      const inputtedEmail = focusEvent.target.innerText;
      if (!inputtedEmail) { // if the div is blank when focus was taken out of it
        this.deleteEmail(index);
        return;
      }

      const emailIsValid = this.checkEmailValidity(inputtedEmail);
      this.emailList.splice(index, 1, {
        email: inputtedEmail,
        valid: emailIsValid,
      });
      this.$emit("update:mails", this.emailList);
    },
    deleteEmailClicked: function(index) {
      this.deleteEmail(index);
    },

    newEmailKeyDown: function(keyboardEvent) {
      const keyPressed = keyboardEvent.key;
      const shiftHeldDown = keyboardEvent.shiftKey;
      const inputtedEmail = keyboardEvent.target.innerText;

      switch (keyPressed) {
        case 'Enter':
        case ';':
          keyboardEvent.preventDefault(); // prevent the keystroke from inputting a character into the div
          if (inputtedEmail) {
            this.addEmail(inputtedEmail);
            keyboardEvent.target.innerHTML = "";
          }
          break;

        case 'Backspace':
          if (!inputtedEmail && this.emailList.length !== 0) { // if the div is blank when backspace is pressed
            this.deleteEmail(this.emailList.length - 1); // delete rightmost email
          }
          break;

        case 'Tab':
          if (shiftHeldDown && this.emailList.length !== 0) { // shift tab is pressed and at least one email has been added
            keyboardEvent.preventDefault(); // prevent tab from moving focus out of the component
            this.shiftEmailTextFocusLeft(-1);
          }

          // if tab was pressed, let the keystroke take the focus out of the component
          break;

        case  ' ':
          keyboardEvent.preventDefault(); 
          break;
      }
    },
    newEmailBlurred: function(focusEvent) {
      const inputtedEmail = focusEvent.target.innerText;
      if (inputtedEmail) {
        this.addEmail(inputtedEmail);
      }

      focusEvent.target.innerHTML = "";
    },

    addEmail: function(inputtedEmail) {
      const emailIsValid = this.checkEmailValidity(inputtedEmail);
      const temp = [
        ...this.emailList,
        {
          email: inputtedEmail,
          valid: emailIsValid,
        },
      ];
      this.emailList = [];
      this.$nextTick(() => {
        this.emailList = temp;
      });

      this.$emit("update:mails", temp);
    },
    deleteEmail: function(index) {
      this.emailList = this.emailList.toSpliced(index, 1);
      // let temp = this.emailList;
      // temp.splice(index, 1);
      // this.emailList = []; // need to do this when removing an email
      // this.$nextTick(() => { // otherwise vue will have trouble updating the UI
      //   this.emailList = temp;
      // });


      this.$emit("update:mails", this.emailList);
    },
    checkEmailValidity: function(text) {
      let emailIsValid = text.match(/(.*?)(\@.{2,})(.*?)(\..{2,})(.*)/g) || false;
      
      if (emailIsValid) {
        emailIsValid = !text.match(/\.{2,}/g) || false;
      }
      if (emailIsValid) {
        emailIsValid = !text.match(/\@{2,}/g) || false;
      }

      return emailIsValid;
    },
    updateEmailListDataMember: function(mails) {
      if (!mails || !Array.isArray(mails)) {
        return;
      }

      this.emailList = [];
      let propIncomplete = false;
      for (const mail of mails) {
        let updatedEmail = {
          email: mail.email,
        };
        if (mail.hasOwnProperty("valid")) {
          updatedEmail.valid = Boolean(mail.valid);
        } else {
          updatedEmail.valid = this.checkEmailValidity(mail.email);
          propIncomplete = true;
        }

        this.emailList.push(updatedEmail);
      }

      if (propIncomplete) { // needed to prevent an infinite loop of updating the prop and data member
        this.$emit("update:mails", this.emailList);
      }
    },

  },
  mounted() {
    this.updateEmailListDataMember(this.mails);
  },
  watch: {
    mails: {
      handler: function(val) {
        this.updateEmailListDataMember(val);
      },
      deep: true,
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.email-input-main {
  border: 1px solid gray;
  max-height: 100px;
  min-height: 100px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: text;
  overflow: auto;
  padding: 3px;
  padding-top: 0;
  font-size: 12px;

  > .email-list {
    display: flex;
    flex-wrap: wrap;

    > div {
      height: fit-content;
      padding: 5px;
      margin-top: 3px;

      &:focus {
        outline: none;
      }
    }

    > .email-container {
      border-radius: 5px;
      width: fit-content;
      display: flex;
      align-items: center;
      margin-left: 3px;

      > .email-text {
        white-space: nowrap;
        min-width: 20px;
        margin-right: 5px;

        &:focus {
          outline: none;
        }
      }
    }
  }

  > .slot-container {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }
}
</style>